import {
  CharityVettingUrlType,
  CountryCodeEnum,
  type CharityVettingUrlTypeValues,
  type CountryCode,
} from 'features/charity-vetting/types/charityVettingTypes';
import { z } from 'zod';

import { esPageSchemas } from '../../country-specific-flows/es-flow/esPageSchemas';
import { frPageSchemas } from '../../country-specific-flows/fr-flow/frPageSchemas';
import { plPageSchemas } from '../../country-specific-flows/pl-flow/plPageSchemas';
import { ptPageSchemas } from '../../country-specific-flows/pt-flow/ptPageSchemas';
import { ukPageSchemas } from '../../country-specific-flows/uk-flow/ukPageSchemas';

import { commonPageSchemas } from './commonPageSchemas';

import type { StepName } from 'features/charity-vetting/utils/validationHandlers';
import type { FieldValues } from 'react-hook-form';
import type { IntlFormatters } from 'react-intl';

export type FormatMessage = IntlFormatters['formatMessage'];

export type PageSchema = z.ZodType<FieldValues, FieldValues>;

/**
 * A per-page Zod schema factory. Validates the slice of `charityOnboarding`
 * owned by a single vetting page. Receives `formatMessage` for i18n error
 * messages and the active country.
 */
export type PageSchemaFactory = (
  formatMessage: FormatMessage,
  countryIsoCode: CountryCode,
) => PageSchema;

export type PageSchemaMap = Partial<Record<CharityVettingUrlTypeValues, PageSchemaFactory>>;

/**
 * Passthrough schema for pages not yet migrated to React Hook Form + Zod.
 * Accepts anything so navigation is never blocked before a page owns a schema.
 */
const passthroughSchema: PageSchemaFactory = () => z.looseObject({});

/**
 * Per-country registries of page schemas, keyed by step name. A country map is
 * consulted first; steps absent there fall back to the country-agnostic
 * `commonPageSchemas`, and finally to `passthroughSchema`.
 *
 * Country maps are needed because several step names (e.g. registration,
 * manual-contact-details) are shared across countries but validate differently
 * — different charity-type enums, phone-number lengths, etc.
 */
const COUNTRY_PAGE_SCHEMAS: Record<CountryCode, PageSchemaMap> = {
  [CountryCodeEnum.GB]: ukPageSchemas,
  [CountryCodeEnum.PT]: ptPageSchemas,
  [CountryCodeEnum.ES]: esPageSchemas,
  [CountryCodeEnum.PL]: plPageSchemas,
  [CountryCodeEnum.FR]: frPageSchemas,
};

export const getPageSchema = (
  stepName: StepName,
  formatMessage: FormatMessage,
  countryIsoCode: CountryCode,
): PageSchema => {
  if (!stepName) {
    return passthroughSchema(formatMessage, countryIsoCode);
  }
  const countryMap = COUNTRY_PAGE_SCHEMAS[countryIsoCode] ?? {};
  const factory = countryMap[stepName] ?? commonPageSchemas[stepName] ?? passthroughSchema;
  return factory(formatMessage, countryIsoCode);
};

export { CharityVettingUrlType };
